// @flow
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import type {YouTubeEvent} from 'react-youtube';
import YoutubePlayer from 'react-youtube';

import type {YouTubePlayer, YouTubeEmbedProps} from './types';

function YouTubeEmbedComponent(
  props: YouTubeEmbedProps,
  ref: React.Ref<YouTubePlayer | null>
) {
  const {
    options = {},
    videoId,
    onEnd,
    onReady,
    onPlay,
    onPause,
    onError,
    onStateChange,
    wrapperClass,
    iframeClass,
    pause,
  } = props;

  const [youtubePlayer, setYoutubePlayer] = useState<YouTubePlayer | null>(
    null
  );

  useImperativeHandle(
    ref,
    () => {
      return youtubePlayer;
    },
    [youtubePlayer]
  );

  useEffect(() => {
    if (!youtubePlayer) return;
    if (pause) {
      youtubePlayer.pauseVideo();
    } else {
      youtubePlayer.playVideo();
    }
  }, [pause, youtubePlayer]);

  const handleEnd = () => {
    const loop = !!options.loop;
    if (!youtubePlayer) return;

    if (loop && options.start) {
      youtubePlayer.seekTo(options.start);
      youtubePlayer.playVideo();
    }

    if (onEnd) {
      onEnd();
    }
  };

  const handleReady = (e: YouTubeEvent) => {
    const event = e;
    if (!event.target) return;
    setYoutubePlayer(event.target);

    if (onReady) {
      onReady(event);
    }
  };

  const handleStateChange = (e: YouTubeEvent) => {
    if (onStateChange) {
      onStateChange(e);
    }
  };

  const handlePlay = (e: YouTubeEvent) => {
    if (onPlay) {
      onPlay(e);
    }
  };

  const handlePause = (e: YouTubeEvent) => {
    if (onPause) {
      onPause(e);
    }
  };

  const handleError = (e: YouTubeEvent) => {
    if (onError) {
      onError(e);
    }
  };

  return (
    <YoutubePlayer
      videoId={videoId}
      className={wrapperClass}
      iframeClassName={iframeClass}
      onEnd={handleEnd}
      onReady={handleReady}
      onPlay={handlePlay}
      onPause={handlePause}
      onError={handleError}
      onStateChange={handleStateChange}
      opts={{
        playerVars: options,
      }}
    />
  );
}

const YouTubeEmbed = forwardRef<YouTubePlayer, YouTubeEmbedProps>(
  YouTubeEmbedComponent
);

export default YouTubeEmbed;
